import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { CibleRattachement, Confidentialite, TypeDocument } from '@artosera/shared';
import { api, requeteMultipart, requeteTelechargement, type AppelApi } from './client.js';
import { estApiError } from './errors.js';
import { cleTableauDeBord } from './tableauDeBord.js';
import type { Pagination } from './types.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';

/**
 * Le coffre — `/v1/g/:slug/documents` (`docs/dev/api.md` § 18, S5). Les
 * formes suivent `apps/api/src/coffre/commun.ts` (`DocumentPresente`,
 * `FichierPresente`) et `journal.ts` (`AccesPresente`) au champ près — même
 * parti pris que `api/ventes.ts` : un seul type par forme, jamais un par
 * écran.
 *
 * Aucune URL signée ne figure jamais sur une fiche ni sur une ligne de liste
 * (§ 18.5) : elle s'obtient exclusivement par `telechargement()`, qui
 * journalise côté serveur AVANT de signer.
 */

export interface FichierCoffre {
  readonly cle: string;
  readonly nom: string;
  readonly typeMime: string;
  readonly octets: number;
  readonly depose: string;
  readonly deposePar: string | null;
}

export interface RattachementDocument {
  readonly type: CibleRattachement;
  readonly id: string;
}

export interface DocumentPresente {
  readonly id: string;
  readonly type: TypeDocument;
  readonly titre: string;
  readonly confidentialite: Confidentialite;
  readonly fichier: FichierCoffre;
  readonly versions: readonly FichierCoffre[];
  readonly rattachements: readonly RattachementDocument[];
  readonly echeanceLe: string | null;
  readonly rappelsJours: readonly number[];
  readonly rappelsEnvoyes: readonly number[];
  readonly etiquettes: readonly string[];
  readonly archive: boolean;
  readonly archiveLe: string | null;
  readonly creeLe: string;
  readonly modifieLe: string;
}

export interface FiltresDocuments {
  type?: readonly TypeDocument[];
  /** `oeuvre:<id>` (ou `artwork:<id>`) — un seul rattachement à la fois (§ 18.2). */
  rattache?: string;
  confidentialite?: Confidentialite;
  echeanceAvant?: string;
  echeanceApres?: string;
  etiquette?: string;
  q?: string;
  inclureArchives?: boolean;
  tri?: 'recent' | 'ancien' | 'titre' | 'echeance';
  page?: number;
  parPage?: number;
}

export interface ReponseListeDocuments {
  readonly documents: readonly DocumentPresente[];
  /** Le nombre de confidentiels que le demandeur n'a pas le droit d'ouvrir — jamais leur nom (§ 18.2). */
  readonly confidentielsMasques: number;
  readonly pagination: Pagination;
}

export interface EcheanceCoffre {
  readonly id: string;
  readonly titre: string;
  readonly type: TypeDocument;
  readonly confidentialite: Confidentialite;
  readonly echeanceLe: string;
  /** Négatif quand l'échéance est passée (§ 18.7, jours civils). */
  readonly joursRestants: number;
}

export interface ReponseEcheances {
  readonly echeances: {
    readonly aVenir: readonly EcheanceCoffre[];
    readonly enRetard: readonly EcheanceCoffre[];
  };
  readonly horizonJours: number;
}

export interface CorpsDepotDocument {
  fichier: File;
  titre: string;
  type?: TypeDocument;
  confidentialite?: Confidentialite;
  echeanceLe?: string;
  /** Jours avant l'échéance, ex. `[30, 7, 1]`. */
  rappelsJours?: readonly number[];
  etiquettes?: readonly string[];
  /** `type:id`, ex. `artwork:<id>`. */
  rattachements?: readonly string[];
}

export interface CorpsMiseAJourDocument {
  titre?: string;
  type?: TypeDocument;
  confidentialite?: Confidentialite;
  echeanceLe?: string | null;
  rappelsJours?: readonly number[];
  etiquettes?: readonly string[];
  rattachements?: readonly RattachementDocument[];
}

export interface TelechargementDocument {
  readonly url: string;
  readonly nom: string;
  readonly typeMime: string;
  readonly octets: number;
  readonly expireLe: string;
  readonly expireDansSecondes: number;
}

export interface AccesPresente {
  readonly id: string;
  readonly documentId: string;
  readonly action: 'ouvert' | 'telecharge' | 'modifie' | 'archive' | 'supprime';
  readonly date: string;
  readonly adresse: string;
  readonly cle: string;
  readonly compteId: string | null;
  readonly compteEmail: string | null;
}

export interface ReponseJournalDocument {
  readonly document: { readonly id: string; readonly titre: string };
  readonly journal: readonly AccesPresente[];
  readonly retentionJours: number;
  readonly pagination: Pagination;
}

function requeteFiltresDocuments(filtres: FiltresDocuments): string {
  const params = new URLSearchParams();
  if (filtres.type && filtres.type.length > 0) params.set('type', filtres.type.join(','));
  if (filtres.rattache) params.set('rattache', filtres.rattache);
  if (filtres.confidentialite) params.set('confidentialite', filtres.confidentialite);
  if (filtres.echeanceAvant) params.set('echeanceAvant', filtres.echeanceAvant);
  if (filtres.echeanceApres) params.set('echeanceApres', filtres.echeanceApres);
  if (filtres.etiquette) params.set('etiquette', filtres.etiquette);
  if (filtres.q) params.set('q', filtres.q);
  if (filtres.inclureArchives) params.set('inclureArchives', 'true');
  params.set('tri', filtres.tri ?? 'recent');
  params.set('page', String(filtres.page ?? 1));
  params.set('parPage', String(filtres.parPage ?? 100));
  return params.toString();
}

function formulaireDepot(corps: CorpsDepotDocument): FormData {
  const donnees = new FormData();
  donnees.set('fichier', corps.fichier);
  donnees.set('titre', corps.titre);
  if (corps.type) donnees.set('type', corps.type);
  if (corps.confidentialite) donnees.set('confidentialite', corps.confidentialite);
  if (corps.echeanceLe) donnees.set('echeanceLe', corps.echeanceLe);
  if (corps.rappelsJours && corps.rappelsJours.length > 0) {
    donnees.set('rappelsJours', corps.rappelsJours.join(','));
  }
  if (corps.etiquettes && corps.etiquettes.length > 0) {
    donnees.set('etiquettes', corps.etiquettes.join(','));
  }
  if (corps.rattachements && corps.rattachements.length > 0) {
    donnees.set('rattachements', corps.rattachements.join(','));
  }
  return donnees;
}

export const coffreApi = {
  lister: (galerie: string, filtres: FiltresDocuments = {}, appel?: AppelApi) =>
    api.get<ReponseListeDocuments>(
      `/g/${encodeURIComponent(galerie)}/documents?${requeteFiltresDocuments(filtres)}`,
      { galerie, ...appel },
    ),
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{ document: DocumentPresente }>(`/g/${encodeURIComponent(galerie)}/documents/${id}`, {
      galerie,
      ...appel,
    }),
  deposer: (galerie: string, corps: CorpsDepotDocument, appel?: AppelApi) =>
    requeteMultipart<{ document: DocumentPresente }>(
      `/g/${encodeURIComponent(galerie)}/documents`,
      formulaireDepot(corps),
      { galerie, ...appel },
    ),
  modifier: (galerie: string, id: string, corps: CorpsMiseAJourDocument, appel?: AppelApi) =>
    api.patch<{ document: DocumentPresente }>(
      `/g/${encodeURIComponent(galerie)}/documents/${id}`,
      {
        ...corps,
        ...(corps.rattachements
          ? { rattachements: corps.rattachements.map((r) => `${r.type}:${r.id}`) }
          : {}),
      },
      { galerie, ...appel },
    ),
  deposerVersion: (galerie: string, id: string, fichier: File, appel?: AppelApi) => {
    const donnees = new FormData();
    donnees.set('fichier', fichier);
    return requeteMultipart<{ document: DocumentPresente }>(
      `/g/${encodeURIComponent(galerie)}/documents/${id}/versions`,
      donnees,
      { galerie, ...appel },
    );
  },
  telechargement: (galerie: string, id: string, version?: string, appel?: AppelApi) => {
    const params = version ? `?version=${encodeURIComponent(version)}` : '';
    return api.get<{ telechargement: TelechargementDocument }>(
      `/g/${encodeURIComponent(galerie)}/documents/${id}/telechargement${params}`,
      { galerie, ...appel },
    );
  },
  archiver: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ document: DocumentPresente }>(
      `/g/${encodeURIComponent(galerie)}/documents/${id}/archiver`,
      {},
      { galerie, ...appel },
    ),
  echeances: (galerie: string, horizonJours?: number, appel?: AppelApi) => {
    const params = horizonJours ? `?horizonJours=${String(horizonJours)}` : '';
    return api.get<ReponseEcheances>(
      `/g/${encodeURIComponent(galerie)}/documents/echeances${params}`,
      { galerie, ...appel },
    );
  },
  journal: (
    galerie: string,
    id: string,
    options: { page?: number; parPage?: number } = {},
    appel?: AppelApi,
  ) => {
    const params = new URLSearchParams();
    params.set('page', String(options.page ?? 1));
    params.set('parPage', String(options.parPage ?? 50));
    return api.get<ReponseJournalDocument>(
      `/g/${encodeURIComponent(galerie)}/documents/${id}/journal?${params.toString()}`,
      { galerie, ...appel },
    );
  },
  journalExporter: (galerie: string, appel?: AppelApi) =>
    requeteTelechargement(`/g/${encodeURIComponent(galerie)}/documents/journal/export`, {
      galerie,
      ...appel,
    }),
};

// ── Erreur de step-up (§ 3, § 18.3) ─────────────────────────────────────────

/**
 * Le confidentiel demande une vérification récente du second facteur
 * (`exigerMfaRecente`, réemployée telle quelle par le coffre) : la réponse
 * porte `403 acces.refuse`, `details.raison: mfa_step_up_requis` — le même
 * repère qu'à l'acceptation d'une invitation (§ 3). L'écran doit alors ouvrir
 * le dialogue TOTP puis rejouer le geste, jamais l'échouer silencieusement.
 */
export function estErreurStepUpRequis(erreur: unknown): boolean {
  if (!estApiError(erreur)) return false;
  const details = erreur.details;
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as { raison?: unknown }).raison === 'mfa_step_up_requis'
  );
}

// ── Hooks React Query ────────────────────────────────────────────────────────

const cleListe = (galerie: string, filtres: FiltresDocuments) =>
  cleMetier(galerie, 'coffre', 'documents', 'liste', filtres);
const cleFiche = (galerie: string, id: string) =>
  cleMetier(galerie, 'coffre', 'documents', 'fiche', id);
const cleEcheances = (galerie: string, horizonJours: number | undefined) =>
  cleMetier(galerie, 'coffre', 'documents', 'echeances', horizonJours ?? null);
const cleJournal = (galerie: string, id: string, page: number) =>
  cleMetier(galerie, 'coffre', 'documents', 'journal', id, page);

export function useDocuments(galerie: string, filtres: FiltresDocuments = {}) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => coffreApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}

export function useDocument(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => coffreApi.obtenir(galerie, id ?? '', appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

export function useEcheancesDocuments(galerie: string, horizonJours?: number) {
  return useQuery({
    queryKey: cleEcheances(galerie, horizonJours),
    queryFn: (contexte) => coffreApi.echeances(galerie, horizonJours, appelDeRequete(contexte)),
  });
}

export function useJournalDocument(
  galerie: string,
  id: string | null,
  options: { page?: number; parPage?: number } = {},
) {
  const page = options.page ?? 1;
  return useQuery({
    queryKey: cleJournal(galerie, id ?? '', page),
    queryFn: (contexte) => coffreApi.journal(galerie, id ?? '', options, appelDeRequete(contexte)),
    enabled: id !== null,
  });
}

function invaliderDocuments(client: ReturnType<typeof useQueryClient>, galerie: string): void {
  void client.invalidateQueries({ queryKey: cleMetier(galerie, 'coffre', 'documents', 'liste') });
  void client.invalidateQueries({
    queryKey: cleMetier(galerie, 'coffre', 'documents', 'echeances'),
  });
  // Les compteurs par type de l'arborescence et les échéances du tableau de
  // bord viennent de l'agrégation (§ 20) : déposer, modifier ou archiver les
  // change, donc ils se refont.
  void client.invalidateQueries({ queryKey: cleTableauDeBord(galerie) });
}

export function useDeposerDocument(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsDepotDocument, appel) => coffreApi.deposer(galerie, corps, appel),
      onSuccess: () => invaliderDocuments(client, galerie),
    }),
  );
}

export function useModifierDocument(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: CorpsMiseAJourDocument }, appel) =>
        coffreApi.modifier(galerie, id, corps, appel),
      onSuccess: (_donnees, variables) => {
        invaliderDocuments(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

export function useDeposerVersionDocument(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, fichier }: { id: string; fichier: File }, appel) =>
        coffreApi.deposerVersion(galerie, id, fichier, appel),
      onSuccess: (_donnees, variables) => {
        invaliderDocuments(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, variables.id) });
      },
    }),
  );
}

/**
 * N'ouvre rien elle-même : rend l'URL signée de deux minutes, à ouvrir par
 * l'appelant (`window.open`). Une mutation, et non une requête : chaque appel
 * journalise (§ 18.5), une nouvelle URL à chaque fois est le comportement
 * correct, jamais une valeur qu'on mettrait en cache.
 */
export function useTelechargerDocument(galerie: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, version }: { id: string; version?: string }, appel) =>
        coffreApi.telechargement(galerie, id, version, appel),
    }),
  );
}

export function useArchiverDocument(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => coffreApi.archiver(galerie, id, appel),
      onSuccess: (_donnees, id) => {
        invaliderDocuments(client, galerie);
        void client.invalidateQueries({ queryKey: cleFiche(galerie, id) });
      },
    }),
  );
}

export function useExporterJournalCoffre(galerie: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: (_variables: void, appel) => coffreApi.journalExporter(galerie, appel),
    }),
  );
}
